import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLang } from '../context/LanguageContext';
import { useNewsData } from '../context/NewsDataContext';
import { pickTeEn } from '../utils/localizedContent.js';
import { API_URL as API, mediaUrl } from '../config/env.js';
import { goNativeTab } from './NativeTabContext';
import { OPEN_DAILY_PAPER_EVENT } from '../utils/paperCache';
import { hapticSelection } from '../utils/haptics';
import './NativeInfoSheet.css';
import './NativeSearchSheet.css';

function matches(q, ...parts) {
  const needle = q.trim().toLowerCase();
  if (!needle) return false;
  return parts.some((p) => String(p || '').toLowerCase().includes(needle));
}

export default function NativeSearchSheet({ open, onClose, onOpenStory }) {
  const { t, lang } = useLang();
  const { stories } = useNewsData();
  const [q, setQ] = useState('');
  const [dates, setDates] = useState([]);

  useEffect(() => {
    if (!open) return undefined;
    const onBack = () => onClose?.();
    window.addEventListener('rk-native-back', onBack);
    fetch(`${API}/api/papers`)
      .then((r) => r.json())
      .then((d) => setDates(d.dates || []))
      .catch(() => {});
    return () => window.removeEventListener('rk-native-back', onBack);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) setQ('');
  }, [open]);

  const storyHits = useMemo(() => {
    const needle = q.trim();
    if (needle.length < 2) return [];
    return (stories || [])
      .filter((s) =>
        matches(
          needle,
          pickTeEn(lang, s.title, s.titleEn),
          pickTeEn(lang, s.excerpt, s.excerptEn),
          pickTeEn(lang, s.category, s.categoryEn),
        ),
      )
      .slice(0, 20);
  }, [q, stories, lang]);

  const paperHits = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (needle.length < 2) return [];
    return dates
      .filter((d) => d.includes(needle) || formatDate(d, lang).toLowerCase().includes(needle))
      .slice(0, 12);
  }, [q, dates, lang]);

  if (!open) return null;

  return createPortal(
    <div className="ios-sheet rk-search" role="dialog" aria-modal="true">
      <header className="ios-sheet__bar">
        <button type="button" className="ios-sheet__back" onClick={onClose} aria-label="Back">
          <svg viewBox="0 0 12 20" width="12" height="20" aria-hidden>
            <path d="M10 2L2 10l8 8" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <h1>{t.nativeSearch}</h1>
        <button type="button" className="ios-sheet__done" onClick={onClose}>
          {t.nativeDone}
        </button>
      </header>
      <div className="ios-sheet__body rk-search__body">
        <label className="rk-search__field">
          <span className="rk-search__icon" aria-hidden>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-3.5-3.5" strokeLinecap="round" />
            </svg>
          </span>
          <input
            autoFocus
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t.nativeSearchPlaceholder}
            enterKeyHint="search"
          />
        </label>

        {q.trim().length < 2 ? (
          <p className="ios-support__lead">{t.nativeSearchHint}</p>
        ) : (
          <>
            {paperHits.length > 0 ? (
              <section className="rk-search__section">
                <h2>{t.navEpaper}</h2>
                <div className="ios-support__group">
                  {paperHits.map((dateKey) => (
                    <button
                      key={dateKey}
                      type="button"
                      className="ios-support__row"
                      onClick={() => {
                        void hapticSelection();
                        onClose?.();
                        goNativeTab('paper');
                        window.setTimeout(() => {
                          window.dispatchEvent(
                            new CustomEvent(OPEN_DAILY_PAPER_EVENT, { detail: { dateKey } }),
                          );
                        }, 60);
                      }}
                    >
                      <span>
                        <strong>{formatDate(dateKey, lang)}</strong>
                        <em>{dateKey}</em>
                      </span>
                      <span className="ios-support__go" aria-hidden />
                    </button>
                  ))}
                </div>
              </section>
            ) : null}

            {storyHits.length > 0 ? (
              <section className="rk-search__section">
                <h2>{t.toiTopNews}</h2>
                <div className="ios-support__group">
                  {storyHits.map((s) => {
                    const title = pickTeEn(lang, s.title, s.titleEn);
                    const cover = mediaUrl(s.cardImage || s.image);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        className="ios-support__row rk-search__story"
                        onClick={() => {
                          void hapticSelection();
                          onClose?.();
                          onOpenStory?.(s.id);
                        }}
                      >
                        <span className="rk-search__story-main">
                          {cover ? <img src={cover} alt="" /> : <span className="rk-search__ph" aria-hidden />}
                          <span>
                            <strong>{title}</strong>
                            <em>{pickTeEn(lang, s.category, s.categoryEn)}</em>
                          </span>
                        </span>
                        <span className="ios-support__go" aria-hidden />
                      </button>
                    );
                  })}
                </div>
              </section>
            ) : null}

            {!paperHits.length && !storyHits.length ? (
              <p className="ios-support__lead">{t.nativeSearchEmpty}</p>
            ) : null}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

function formatDate(dateKey, lang) {
  try {
    return new Date(`${dateKey}T00:00:00`).toLocaleDateString(
      lang === 'te' ? 'te-IN' : lang === 'ta' ? 'ta-IN' : lang === 'kn' ? 'kn-IN' : 'en-IN',
      { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' },
    );
  } catch {
    return dateKey;
  }
}
